import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';

import { ComparisonOperator, LogicalType, QueryNodeType } from '@blaze-shared/api-contracts-domain';
import { sentryLog } from '@blaze-shared/logger-util';
import { DatasetRowsService, SchemaService } from '@blaze-shared/schema-data-access';
import { DatasetRow, DatasetSchema, isDatasetLinkValue } from '@blaze-shared/schema-domain';

export interface PageContextFieldData {
  dataRow: DatasetRow | null;
  dataRowSchema: DatasetSchema;
}

@Injectable({
  providedIn: 'root',
})
export class NavigatePageContextFieldService {
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly schemaService = inject(SchemaService);

  getPageContextFieldData(
    dataRow: DatasetRow | null,
    mainDatasetSchema: DatasetSchema | undefined,
    pageContextField: string | undefined,
  ): Observable<PageContextFieldData | undefined> {
    const contextColumnName = this.schemaService.getColumnName(pageContextField);
    if (!contextColumnName) {
      return of(undefined);
    }

    const contextColumnData = dataRow?.[contextColumnName];
    const contextColumnRowId = isDatasetLinkValue(contextColumnData)
      ? contextColumnData.rowId
      : null;

    // If the main dataset schema is not provided, we can use the node input as the context data
    if (!mainDatasetSchema?.id) {
      throw new Error(`Main dataset schema not found. Cannot extract context column data.`);
    }

    if (!contextColumnRowId) {
      return of({
        dataRow: null,
        dataRowSchema: mainDatasetSchema,
      });
    }

    // Fetch the related row based on the context column row ID
    return this.fetchRelatedRow(mainDatasetSchema, contextColumnRowId, pageContextField).pipe(
      map((result) => {
        return { dataRow: result, dataRowSchema: mainDatasetSchema };
      }),
    );
  }

  private fetchRelatedRow(
    mainDatasetSchema: DatasetSchema,
    contextColumnRowId: string,
    pageContextField: string | undefined,
  ): Observable<DatasetRow | null> {
    // Get ID column name from the main dataset schema
    const idColumn = mainDatasetSchema.columns.find((col) => col.isId)?.name;

    if (!idColumn) {
      sentryLog(
        `ID column not found in the main dataset schema "${mainDatasetSchema.id}"`,
        'error',
      );

      return of(null);
    }

    // Fetch row from dataset
    return this.datasetRowsService
      .get({
        datasetId: mainDatasetSchema.id,
        filterQuery: {
          nodeType: QueryNodeType.Logical,
          logicalType: LogicalType.And,
          children: [
            {
              column: idColumn,
              displayInfos: [],
              isFormulaValue: false,
              isOptional: false,
              nodeType: QueryNodeType.Comparison,
              operator: ComparisonOperator.Eq,
              value: contextColumnRowId,
            },
          ],
        },
      })
      .pipe(
        map((result) => {
          if (!result?.length) {
            sentryLog(
              `No data found for page context field "${pageContextField}" (${contextColumnRowId})`,
              'error',
            );
          }

          if (result.length > 1) {
            sentryLog(
              `More than one data found for page context "${pageContextField}" (${contextColumnRowId})`,
              'warning',
            );
          }

          // Take the first match
          return result[0] ?? null;
        }),
      );
  }
}
